import { BrowserSessionApiClient } from "./browserSessionApiClient";

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || "/api").replace(/\/+$/, "");

export type ProjectClientNotificationDiscipline = "structural" | "civil";

export type ProjectClientNotificationTemplate = {
  key: string;
  discipline: ProjectClientNotificationDiscipline;
  label: string;
  shortDescription: string;
  defaultShortDescription?: string;
  detailUpdatedAt?: string | null;
  detailUpdatedBy?: string | null;
  ownerName: string;
  order: number;
  defaultMessage: string;
  subject: string;
  serviceMentionText: string;
};

export type ProjectClientNotificationHistoryItem = {
  notificationId: number;
  clientRequestId: string;
  discipline: ProjectClientNotificationDiscipline;
  stageKey: string;
  stageLabel: string;
  recipientEmail: string;
  recipientName: string | null;
  senderEmail: string;
  senderName: string;
  subject: string;
  messageText: string;
  personalNote: string | null;
  includeServiceMention: boolean;
  serviceMentionText: string | null;
  status: "Sending" | "Sent" | "Failed" | string;
  attemptedAt: string | null;
  sentAt: string | null;
  failedAt: string | null;
  sentBy: string | null;
};

export type ProjectClientNotificationProgressItem = {
  discipline: ProjectClientNotificationDiscipline;
  currentStageKey: string;
  updatedAt: string | null;
  updatedBy: string | null;
};

export type ProjectClientNotificationWorkspace = {
  project: {
    projectId: number;
    projectCode: string;
    projectAddress: string;
    clientName: string | null;
    clientEmail: string | null;
  };
  templates: ProjectClientNotificationTemplate[];
  progress: ProjectClientNotificationProgressItem[];
  history: ProjectClientNotificationHistoryItem[];
};

export type SendProjectClientNotificationInput = {
  templateKey: string;
  messageText: string;
  personalNote?: string | null;
  includeServiceMention: boolean;
  clientRequestId: string;
};

function unwrapData<T>(response: { data?: T } | T): T {
  return response && typeof response === "object" && "data" in response
    ? (response as { data: T }).data
    : response as T;
}

class ProjectClientNotificationService {
  private readonly browserClient = new BrowserSessionApiClient(API_BASE_URL);

  async getWorkspace(projectId: number): Promise<ProjectClientNotificationWorkspace> {
    const normalizedProjectId = Math.trunc(Number(projectId));
    if (!Number.isFinite(normalizedProjectId) || normalizedProjectId <= 0) {
      throw new Error("A valid projectId is required");
    }

    const response = await this.browserClient.request<
      { data?: ProjectClientNotificationWorkspace } | ProjectClientNotificationWorkspace
    >(`${API_BASE_URL}/v1/projects/${normalizedProjectId}/client-notifications`, {
      method: "GET",
    });
    return unwrapData(response);
  }

  async send(projectId: number, input: SendProjectClientNotificationInput) {
    const normalizedProjectId = Math.trunc(Number(projectId));
    if (!Number.isFinite(normalizedProjectId) || normalizedProjectId <= 0) {
      throw new Error("A valid projectId is required");
    }

    const response = await this.browserClient.request(`${API_BASE_URL}/v1/projects/${normalizedProjectId}/client-notifications`, {
      method: "POST",
      body: input,
    });
    return unwrapData(response);
  }

  async setCurrentStage(
    projectId: number,
    discipline: ProjectClientNotificationDiscipline,
    currentStageKey: string
  ): Promise<{ projectId: number; progress: ProjectClientNotificationProgressItem[] }> {
    const normalizedProjectId = Math.trunc(Number(projectId));
    if (!Number.isFinite(normalizedProjectId) || normalizedProjectId <= 0) {
      throw new Error("A valid projectId is required");
    }

    const response = await this.browserClient.request<{
      data?: { projectId: number; progress: ProjectClientNotificationProgressItem[] };
    } | { projectId: number; progress: ProjectClientNotificationProgressItem[] }>(
      `${API_BASE_URL}/v1/projects/${normalizedProjectId}/client-notifications`,
      {
        method: "PATCH",
        body: { discipline, currentStageKey },
      }
    );
    return unwrapData(response);
  }

  async saveStageDetail(
    projectId: number,
    discipline: ProjectClientNotificationDiscipline,
    stageKey: string,
    detailText: string
  ): Promise<{
    projectId: number;
    templates: ProjectClientNotificationTemplate[];
  }> {
    const normalizedProjectId = Math.trunc(Number(projectId));
    if (!Number.isFinite(normalizedProjectId) || normalizedProjectId <= 0) {
      throw new Error("A valid projectId is required");
    }

    const response = await this.browserClient.request<{
      data?: { projectId: number; templates: ProjectClientNotificationTemplate[] };
    } | { projectId: number; templates: ProjectClientNotificationTemplate[] }>(
      `${API_BASE_URL}/v1/projects/${normalizedProjectId}/client-notifications`,
      {
        method: "PATCH",
        body: { discipline, stageKey, detailText },
      }
    );
    return unwrapData(response);
  }
}

export const projectClientNotificationService = new ProjectClientNotificationService();
